import React, { useState, useMemo, useCallback } from 'react';
import {
    Modal,
    SpaceBetween,
    Button,
    Popover,
    Tabs,
    KeyValuePairs,
    Header,
    StatusIndicator,
    Spinner,
} from '@amzn/awsui-components-react';
import HtmlRenderer from '../../../tiptap/HtmlRenderer';
import Styled from './styled';
import { GetCsqaPayload } from '../types';
import { reviewTemplate } from '../reviewTemplate';
import { UserPermissions } from '../../../common/UserPermissions';

interface ViewDetailsModalProps {
    loading: boolean;
    visible: boolean;
    onDismiss: () => void;
    selectedReview: GetCsqaPayload | null;
    userPermissions: Set<string>;
    onEditReview?: () => void;
    onDeleteReview?: () => void;
    viewOnly: boolean;
}

const ViewDetailsModal: React.FC<ViewDetailsModalProps> = ({
    loading,
    visible,
    onDismiss,
    selectedReview,
    userPermissions,
    onEditReview,
    onDeleteReview,
    viewOnly,
}) => {

    // If review details are not yet available, show the spinner
    if (loading || !selectedReview) {
        return (
            <Modal
                visible={visible}
                onDismiss={onDismiss}
                header='Evaluation Details'
                footer={<Button onClick={onDismiss}>Close</Button>}
            >
                <Styled.LoadingWrapper>
                    <Spinner size='large' />
                </Styled.LoadingWrapper>
            </Modal>
        );
    }
    // Destructure the new payload parts
    const { identifiers, qualityQuestions, biQuestions, notes } = selectedReview;
    const [activeTab, setActiveTab] = useState<string>(
        qualityQuestions.length > 0 ? qualityQuestions[0].questionId : ''
    );

    // Build key–value pairs for the identifiers
    const identifierItems = useMemo(() => [
        { label: 'Evaluation Date', value: identifiers.evaluationDate },
        { label: 'Evaluator Email', value: identifiers.evaluatorEmail },
        { label: 'Evaluation Type', value: identifiers.evaluationType },
        { label: 'Monitoring Method', value: identifiers.monitoringMethod },
        { label: 'Associate Email', value: identifiers.associateEmail },
        { label: 'Associate Site', value: identifiers.associateSite },
        { label: 'Supervisor Email', value: identifiers.supervisorEmail },
        { label: 'Contact ID', value: identifiers.contactId },
        { label: 'Form ID', value: identifiers.formId },
        { label: 'Coaching Status', value: identifiers.coachingStatus },
    ], [identifiers]);

    // Build quality question tabs using the qualityQuestions array.
    // We lookup each question’s template from reviewTemplate.
    const qualityTabs = useMemo(() => {
        return qualityQuestions.map((q, index) => {
            const template = reviewTemplate.qualityQuestions[q.questionId];
            // Determine a simple indicator for the answer
            let indicator;
            if (q.value === 'Yes') indicator = <span style={{ color: 'green' }}>✓</span>;
            else if (q.value === 'No') indicator = <span style={{ color: 'red' }}>✗</span>;
            else if (q.value === 'N/A') indicator = <span style={{ color: '#999' }}>N/A</span>;

            // Determine guidelines:
            // Start with the main question guidelines.
            let guidelines = {
                intent: template.intent,
                passCriteria: template.passCriteria,
                failCriteria: template.failCriteria,
            };
            // If the answer is 'No' and there are sub-attributes selected,
            // then update the guidelines to use the first checked sub-attribute.
            if (q.value === 'No' && q.subAttributes && q.subAttributes.length > 0) {
                const checkedSub = q.subAttributes.find(sa => sa.value === true);
                if (checkedSub && template.subAttributes && template.subAttributes[checkedSub.subAttributeId]) {
                    guidelines = {
                        intent: template.subAttributes[checkedSub.subAttributeId].intent,
                        passCriteria: template.subAttributes[checkedSub.subAttributeId].passCriteria,
                        failCriteria: template.subAttributes[checkedSub.subAttributeId].failCriteria,
                    };
                }
            }

            return {
                id: q.questionId,
                label: (
                    <>
                        Question {index + 1} {indicator}
                    </>
                ),
                content: (
                    <SpaceBetween direction='vertical' size='m'>
                        <Styled.ReviewLabel>{template.question}</Styled.ReviewLabel>
                        <Styled.ReviewValue>Answer: {q.value}</Styled.ReviewValue>
                        {q.value === 'No' && q.subAttributes && q.subAttributes.length > 0 && (
                            <div style={{ marginTop: '1rem' }}>
                                <strong>Reason:</strong>
                                <ul>
                                    {q.subAttributes.filter(sa => sa.value === true).map(sa => (
                                        <li key={sa.subAttributeId}>{sa.label}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <KeyValuePairs
                            columns={3}
                            minColumnWidth={300}
                            items={[
                                { label: 'Intent', value: <HtmlRenderer html={guidelines.intent} /> },
                                { label: 'Pass Criteria', value: <HtmlRenderer html={guidelines.passCriteria} /> },
                                { label: 'Fail Criteria', value: <HtmlRenderer html={guidelines.failCriteria} /> },
                            ]}
                        />
                    </SpaceBetween>
                ),
            };
        });
    }, [qualityQuestions]);

    // Render BI questions (as a simple list)
    const renderBiQuestions = useCallback(() => {
        if (!biQuestions || biQuestions.length === 0) return <div>No Business Impacting Questions.</div>;
        return (
            <SpaceBetween direction='vertical' size='m'>
                {biQuestions.map(bq => (
                    <div key={bq.questionId} style={{ borderBottom: '1px solid #ccc', paddingBottom: '1rem', marginBottom: '1rem' }}>
                        <Styled.ReviewLabel>{bq.label}</Styled.ReviewLabel>
                        <Styled.ReviewValue>
                            Answer: {bq.value}{bq.subvalue ? ` (${bq.subvalue})` : ''}
                        </Styled.ReviewValue>
                    </div>
                ))}
            </SpaceBetween>
        );
    }, [biQuestions]);

    const handleCopyLink = useCallback(() => {
        const link = `${window.location.origin}/?id=${selectedReview.id}&createdTimestamp=${selectedReview.createdTimestamp}`;
        navigator.clipboard.writeText(link)
            .catch((err) => {
                console.error('Error copying link:', err);
            });
    }, [selectedReview]);

    return (
        <Modal
            id={`reviewDetails${identifiers.formId}`}
            visible={visible}
            size='max'
            onDismiss={onDismiss}
            header='Evaluation Details'
            footer={
                <SpaceBetween direction='horizontal' size='m'>
                    {!identifiers.deleted && !viewOnly && (
                        <Popover
                            dismissButton={false}
                            position='top'
                            size='small'
                            triggerType='custom'
                            content={
                                <StatusIndicator type='success'>
                                    Evaluation link copied
                                </StatusIndicator>
                            }
                        >
                            <Button iconName='copy' onClick={handleCopyLink}>
                                Copy Evaluation Link
                            </Button>
                        </Popover>
                    )}
                    {userPermissions.has(UserPermissions.EditCsqa) && !identifiers.deleted && !viewOnly && (
                        <>
                            <Button onClick={onEditReview} variant='primary'>
                                Edit Evaluation
                            </Button>
                            <Button
                                variant='primary'
                                onClick={onDeleteReview}
                            >
                                Delete Evaluation
                            </Button>
                        </>
                    )}
                    <Button onClick={onDismiss}>Close</Button>
                </SpaceBetween>
            }
        >
            <Styled.ReviewDetailsContainer>
                <KeyValuePairs columns={2} items={identifierItems} />
            </Styled.ReviewDetailsContainer>
            <hr />
            <Styled.ReviewDetailsContainer>
                <Tabs
                    tabs={qualityTabs}
                    activeTabId={activeTab}
                    onChange={(e) => setActiveTab(e.detail.activeTabId)}
                />
            </Styled.ReviewDetailsContainer>
            <hr />
            <Styled.ReviewDetailsContainer>
                <Header variant='h2'>Business Impact</Header>
                {renderBiQuestions()}
            </Styled.ReviewDetailsContainer>
            <hr />
            <Styled.ReviewDetailsContainer>
                <Header variant='h2'>Notes / Comments</Header>
                <div>
                    <HtmlRenderer html={notes || 'N/A'} />
                </div>
            </Styled.ReviewDetailsContainer>
        </Modal>
    );
};

export default ViewDetailsModal;
